import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../../util/log';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';

// ================ Async Thunks ================ //

const fetchCategoryListingsPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, rejectWithValue, dispatch } = thunkAPI;
  const { categoryId, conditions, materials, priceMin, priceMax, keywords, origin, distance } = arg;
  const originMaybe = origin ? { origin } : {};
  const distanceMaybe = origin && distance ? { distance } : {};

  // Build optional filter params
  const conditionFilter =
    conditions && conditions.length > 0 ? { pub_condition: `has_any:${conditions.join(',')}` } : {};
  const materialFilter =
    materials && materials.length > 0 ? { pub_categoryLevel2: `has_any:${materials.join(',')}` } : {};
  const priceFilter = (() => {
    const min = priceMin != null ? Math.round(priceMin * 100) : null;
    const max = priceMax != null ? Math.round(priceMax * 100) + 1 : null;
    if (min != null && max != null) return { price: `${min},${max}` };
    if (min != null) return { price: `${min},` };
    if (max != null) return { price: `0,${max}` };
    return {};
  })();
  const keywordsMaybe = keywords?.trim() ? { keywords: keywords.trim() } : {};

  return sdk.listings
    .query({
      pub_categoryLevel1: categoryId,
      ...conditionFilter,
      ...materialFilter,
      ...priceFilter,
      ...keywordsMaybe,
      ...originMaybe,
      ...distanceMaybe,
      perPage: 24,
      page: 1,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.condition',
        'publicData.location',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'metadata.reviewAvg',
        'metadata.reviewCount',
      ],
      'fields.user': ['profile.metadata'],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-small',
        'variants.scaled-medium',
      ],
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const listingIds = response.data.data.map(l => l.id);
      return { listingIds };
    })
    .catch(error => {
      log.error(error, 'category-page-fetch-listings-failed');
      return rejectWithValue(storableError(error));
    });
};

export const fetchCategoryListings = createAsyncThunk(
  'CategoryPage/fetchCategoryListings',
  fetchCategoryListingsPayloadCreator
);

// ================ Slice ================ //

const categoryPageSlice = createSlice({
  name: 'CategoryPage',
  initialState: {
    categoryId: null,
    listingIds: [],
    fetchInProgress: false,
    fetchError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCategoryListings.pending, (state, action) => {
        state.fetchInProgress = true;
        state.fetchError = null;
        state.categoryId = action.meta.arg.categoryId;
      })
      .addCase(fetchCategoryListings.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.listingIds = action.payload.listingIds;
      })
      .addCase(fetchCategoryListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default categoryPageSlice.reducer;

// ================ loadData ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { categoryId } = params;
  return dispatch(fetchCategoryListings({ categoryId }));
};
